import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { createPortal } from 'react-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import schedulingService from '../../../services/scheduling.service';
import troubleTicketsService from '../../../services/troubleTickets.service';
import repairOrdersService from '../../../services/repairOrders.service';
import { RescheduleHistoryItem } from '../../../types/scheduling';
import { DriverLookup } from '../../../types/repairOrders';
import DateTimePicker from '../../../components/DateTimePicker';
import { DOWNSTREAM_SKIP_REASON_LABELS } from '../../../utils/legFieldGuards';
import TypeAhead, { TypeAheadOption } from '../../../components/TypeAhead';
import RecordHistoryDrawer from '../../../components/RecordHistoryDrawer';
import ServiceFailureTypeHelp from '../../../components/ServiceFailureTypeHelp';
import { isThdBillto } from '../../../constants/billtosCustomers';
import {
  SERVICE_FAILURE_TYPE_OPTIONS,
  SERVICE_FAILURE_TYPE_REQUIRED_MESSAGE,
  isControllableReason,
  serviceFailureTypeMissing,
} from '../../../constants/serviceFailureTypes';
import { useToast } from '../../../contexts/ToastContext';

/** Friendly labels for the reschedule columns logged to cms.history
 *  (dbtable='reschedule'). Used by the per-record History sub-drawer. */
const SF_HISTORY_FIELD_LABELS: Record<string, string> = {
  servicefailuredate: 'Service Failure Date',
  olddate: 'Old Date',
  newdate: 'New Date',
  reason: 'Reason',
  servicefailuretype: 'Service Failure Type',
  thd_reason: 'THD Reason',
  responsibledriver: 'Responsible Driver',
  comments: 'Comments',
  coded_by: 'Coded By',
  reviewedby: 'Reviewed By',
  revieweddatetime: 'Reviewed Date/Time',
};

export interface RescheduleEditModalProps {
  pronumber: number;
  /** Order's billto — gates the THD Reason dropdown. */
  billTo?: number | null;
  /** Present = edit mode, absent = create mode. */
  reschedule?: RescheduleHistoryItem;
  /**
   * Seeds Old/New Date in create mode so the caller can lock them in.
   * The fields render only when at least one is set (always readonly).
   * Used by the delivery-appt-date change flow; ignored in edit mode.
   */
  initialOldDate?: string | null;
  initialNewDate?: string | null;
  /**
   * Seeds the Service Failure Date in create mode (YYYY-MM-DD). Overrides the
   * per-mode default (`initialOldDate` in `required` mode, today otherwise).
   * Used e.g. by the Uncoded-planned "add SF" flow to default the date to the
   * planned delivery date.
   */
  initialServiceFailureDate?: string | null;
  /**
   * Marks the delivery-appt-date change flow: the reschedule MUST be recorded
   * for the appointment change to stand. It defaults the Service Failure Date
   * to `initialOldDate` — the appointment being removed or moved IS the
   * failure date — and lets an identical reschedule reuse the existing row
   * instead of 409ing.
   *
   * It does NOT block dismissal. Cancel / X / backdrop abandon the reschedule,
   * and the caller reverts the appt-date edit that opened this modal — see
   * LegDateCell, which never stages the leg edit until onSaved fires.
   */
  required?: boolean;
  /**
   * Delivery-appt CLEAR only: the downstream legs going with it. Clearing a
   * delivery appointment also clears the appointment dates on the legs after
   * the delivery leg, so the pro doesn't reappear in the LC "Missing
   * Appointment" bucket — see planDownstreamApptClears. Listed here rather than
   * applied silently, and `skipped` matters most: those legs keep their dates
   * (already dispatched or already run), which leaves the pro in the bucket.
   */
  downstreamApptClears?: {
    cleared: number[];
    skipped: Array<{ legnum: number; reason: 'driver' | 'arrived' | 'scope' }>;
  };
  onClose: () => void;
  onSaved: () => void;
}

/** "leg 4" / "legs 3, 4" */
const legList = (legnums: number[]): string =>
  `leg${legnums.length === 1 ? '' : 's'} ${legnums.join(', ')}`;

const isoToDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  // Accept either YYYY-MM-DD or full ISO datetime; treat as local-noon
  // to avoid timezone surprises shifting the displayed day.
  const datePart = iso.slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateToIso = (d: Date | null): string | null => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatReadonlyDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const RescheduleEditModal: React.FC<RescheduleEditModalProps> = ({
  pronumber,
  billTo,
  reschedule,
  initialOldDate,
  initialNewDate,
  initialServiceFailureDate,
  required = false,
  downstreamApptClears,
  onClose,
  onSaved,
}) => {
  // Show the THD Reason dropdown only on THD orders. If billTo isn't a
  // THD bill-to, we hide the field — but we don't clear an existing
  // thd_reason on save (handled by leaving the stored value alone).
  const showThdReason = isThdBillto(billTo);
  const isCreate = !reschedule;
  const { showToast } = useToast();

  const [serviceFailureDate, setServiceFailureDate] = useState<Date | null>(() => {
    if (reschedule?.servicefailuredate) return isoToDate(reschedule.servicefailuredate);
    // Caller-seeded date (e.g. Uncoded-planned "add SF" defaults to the planned date).
    if (!reschedule && initialServiceFailureDate) return isoToDate(initialServiceFailureDate);
    // Delivery-appt-change (required) path: the service failure happened on the
    // appointment being removed/moved, so default to the OLD date rather than
    // leaving it blank. Matches the SFs written without a prompt elsewhere —
    // the LC "Clear All Leg Appt Dates" batch (clear_appt_dates_service) and
    // the Reschedule Requested trigger (reschedule_trigger_service) both store
    // the cleared destapptdate as servicefailuredate. Still editable, so the
    // user can move it if the failure really landed on another day.
    if (!reschedule && required && initialOldDate) return isoToDate(initialOldDate);
    // Manual + New from the panel: default to today as a convenience.
    if (!reschedule && !required) return new Date();
    return null;
  });
  // Old/New Date are display-only here: edit-mode shows the stored value;
  // create-mode shows whatever the caller seeded (typically only the
  // delivery-appt-date change flow). The + New button on the panel passes
  // nothing, so these stay null and the row is hidden entirely.
  const oldDate = isoToDate(reschedule?.olddate ?? initialOldDate);
  const newDate = isoToDate(reschedule?.newdate ?? initialNewDate);
  const [reason, setReason] = useState<string>(reschedule?.reason ?? '');
  const [serviceFailureType, setServiceFailureType] = useState<string>(
    reschedule?.servicefailuretype ?? '',
  );
  const [thdReason, setThdReason] = useState<string>(reschedule?.thd_reason ?? '');
  const [responsibledriver, setResponsibleDriver] = useState<string>(
    reschedule?.responsibledriver ?? '',
  );
  const [comments, setComments] = useState<string>(reschedule?.comments ?? '');

  const [reasonOptions, setReasonOptions] = useState<string[]>([]);
  const [thdReasonOptions, setThdReasonOptions] = useState<string[]>([]);
  const [drivers, setDrivers] = useState<DriverLookup[]>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-record history sub-drawer (edit mode only — an existing reschedule
  // has a recno to scope the history by).
  const [historyOpen, setHistoryOpen] = useState(false);

  // Fetch dropdown options once on mount.
  useEffect(() => {
    const controller = new AbortController();
    schedulingService
      .listRescheduleReasons(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setReasonOptions(list);
      })
      .catch(() => { /* leave list empty if fetch fails */ });
    troubleTicketsService
      .listThdReasons(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setThdReasonOptions(list);
      })
      .catch(() => { /* leave list empty if fetch fails */ });
    // Driver lookup for the Responsible Driver field — same source as the
    // Repair Orders "Reported By" picker (safety.driver).
    repairOrdersService
      .getDrivers()
      .then((res) => {
        if (!controller.signal.aborted) setDrivers(res.data || []);
      })
      .catch(() => { /* leave list empty if fetch fails */ });
    return () => controller.abort();
  }, []);

  // Driver options keyed by unit number — that's what we store in
  // responsibledriver. Drivers without a unit number can't be saved as a
  // unit number, so they're omitted.
  const driverOptions = useMemo<TypeAheadOption[]>(
    () =>
      drivers
        .filter((d) => d.unitnumber)
        .map((d) => {
          const toTitleCase = (s: string) =>
            s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
          const name = toTitleCase(`${d.firstname} ${d.lastname}`.trim());
          return {
            value: d.unitnumber,
            label: name ? `${d.unitnumber} - ${name}` : d.unitnumber,
            description: d.active ? undefined : '⚠ Inactive',
            group: d.active ? 'Active' : 'Inactive',
          };
        }),
    [drivers],
  );

  const handleSave = useCallback(async () => {
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    // A controllable failure with no type never counts against OTD, so it must
    // say what failed before it can be saved. Uncontrollable reasons may skip it.
    if (serviceFailureTypeMissing(reason, serviceFailureType)) {
      setError(SERVICE_FAILURE_TYPE_REQUIRED_MESSAGE);
      return;
    }
    if (showThdReason && !thdReason.trim()) {
      setError('THD Reason is required.');
      return;
    }
    if (!comments.trim()) {
      setError('Comments are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isCreate) {
        await schedulingService.createReschedule(pronumber, {
          olddate: dateToIso(oldDate),
          newdate: dateToIso(newDate),
          servicefailuredate: dateToIso(serviceFailureDate),
          reason: reason.trim(),
          servicefailuretype: serviceFailureType.trim(),
          thd_reason: thdReason.trim(),
          responsibledriver: responsibledriver.trim(),
          comments: comments.trim(),
          // The required (appt-date-change) path may legitimately re-trigger an
          // identical reschedule — let it reuse the existing row. The manual
          // + New path leaves this off so a duplicate surfaces as a 409.
          allow_existing: required,
        });
      } else {
        await schedulingService.updateReschedule(reschedule!.recno, {
          servicefailuredate: dateToIso(serviceFailureDate),
          reason: reason.trim() || null,
          servicefailuretype: serviceFailureType.trim() || null,
          thd_reason: thdReason.trim() || null,
          responsibledriver: responsibledriver.trim() || null,
          comments: comments.trim() || null,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error('Error saving reschedule:', err);
      const detail =
        err?.response?.data?.detail || err?.message || 'Failed to save reschedule';
      // Surface save failures (notably the duplicate-service-failure 409) as an
      // error toast so the user can't mistake a blocked save for a successful
      // one. The modal stays open and keeps the inline message for context.
      showToast({ type: 'error', message: detail });
      setError(detail);
    } finally {
      setSaving(false);
    }
  }, [
    isCreate,
    pronumber,
    reschedule,
    oldDate,
    newDate,
    serviceFailureDate,
    reason,
    serviceFailureType,
    thdReason,
    showThdReason,
    responsibledriver,
    comments,
    required,
    onSaved,
    onClose,
    showToast,
  ]);

  const title = isCreate ? `New Service Failure — Pro ${pronumber}` : `Service Failure #${reschedule!.recno}`;

  // Rendered only while open. Alt+S saves the service failure, Alt+C closes —
  // both mirroring the footer buttons' saving guard.
  useAltHotkeys(
    { S: saving ? null : handleSave, C: saving ? null : onClose },
    undefined,
    { layer: true },
  );

  // Mounted only while open; unmount resets the drag offset.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reschedule-edit-modal-title"
      onMouseDown={(e) => {
        // Dismissing is allowed in every mode, required included: the caller
        // undoes the appt-date change that opened it, so no appointment ever
        // moves without a reschedule row behind it.
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col border border-slate-200 dark:border-gray-700"
      >
        <ModalHeader
          title={title}
          titleId="reschedule-edit-modal-title"
          onClose={saving ? undefined : onClose}
          dragHandleProps={dragHandleProps}
          actions={
            /* Quick view of this service failure's change history. Edit mode
               only — an existing reschedule has a recno to scope by. */
            !isCreate && reschedule ? (
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-300 hover:text-white"
                title="View change history"
              >
                <ClockIcon className="h-4 w-4" />
                History
              </button>
            ) : undefined
          }
        />

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300 flex-1">{error}</p>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300"
                aria-label="Dismiss error"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}

          {!isCreate && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
              <div>
                <div className="font-medium text-muted">Created By</div>
                <div className="text-body mt-0.5">{reschedule!.createdby || '-'}</div>
              </div>
              <div>
                <div className="font-medium text-muted">Create Date</div>
                <div className="text-body mt-0.5">
                  {formatReadonlyDateTime(reschedule!.createdatetime)}
                </div>
              </div>
              <div>
                <div className="font-medium text-muted">Coded By</div>
                <div className="text-body mt-0.5">{reschedule!.coded_by || '-'}</div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {/* Downstream appt clears riding along with a delivery-appt clear.
                Shown before the form so the LC sees the blast radius (and,
                more importantly, what did NOT clear) before coding anything. */}
            {downstreamApptClears &&
              (downstreamApptClears.cleared.length > 0 ||
                downstreamApptClears.skipped.length > 0) && (
                <div
                  data-testid="downstream-appt-clears"
                  className="rounded border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 px-3 py-2 space-y-1"
                >
                  {downstreamApptClears.cleared.length > 0 && (
                    <p className="text-xs text-body">
                      Appointment dates on{' '}
                      <span className="font-semibold">
                        {legList(downstreamApptClears.cleared)}
                      </span>{' '}
                      will be cleared with this one.
                    </p>
                  )}
                  {downstreamApptClears.skipped.length > 0 && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Keeping the appointment on{' '}
                      {downstreamApptClears.skipped
                        .map((s) => `leg ${s.legnum} (${DOWNSTREAM_SKIP_REASON_LABELS[s.reason]})`)
                        .join(', ')}
                      . This pro will stay in Missing Appointment until those are cleared.
                    </p>
                  )}
                </div>
              )}

            {/* Old/New Date shown on edit only (viewing an existing reschedule).
                Hidden on create; the seeded values still save via oldDate/newDate. */}
            {!isCreate && (oldDate || newDate) && (
              <div className="grid grid-cols-2 gap-x-4">
                <div>
                  <label className="block text-xs font-medium text-muted mb-1">
                    Old Date
                  </label>
                  <div className="text-sm text-body px-2 py-1.5 border border-slate-200 dark:border-gray-700 rounded bg-slate-50 dark:bg-gray-900">
                    {oldDate
                      ? oldDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
                      : '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted mb-1">
                    New Date
                  </label>
                  <div className="text-sm text-body px-2 py-1.5 border border-slate-200 dark:border-gray-700 rounded bg-slate-50 dark:bg-gray-900">
                    {newDate
                      ? newDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
                      : '-'}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label
                htmlFor="rs-servicefailuredate"
                className="block text-xs font-medium text-muted mb-1"
              >
                Service Failure Date
              </label>
              <DateTimePicker
                mode="date"
                selectionType="single"
                inputVariant="modal"
                usePortal
                autoApply
                labelType="none"
                // DateTimePicker takes no id, so the label above can't reach the
                // input by htmlFor — name it here instead.
                ariaLabel="Service Failure Date"
                value={serviceFailureDate}
                onChange={(val) =>
                  setServiceFailureDate(val instanceof Date ? val : null)
                }
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                Reason<span className="text-red-500 ml-1">*</span>
              </label>
              <TypeAhead
                options={reasonOptions.map((r) => ({ value: r, label: r }))}
                value={reason}
                onChange={(next) => setReason(next)}
                onClear={() => setReason('')}
                placeholder="Select reason…"
                disabled={saving}
                showSearch
                flagInvalidValue
              />
            </div>

            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Service Failure Type
                  {/* Required whenever the reason is controllable — only Delivery
                      Failures count against OTD, so an untyped C: row is a hole. */}
                  {isControllableReason(reason) && <span className="text-red-500 ml-1">*</span>}
                </label>
                <ServiceFailureTypeHelp />
              </div>
              <TypeAhead
                options={SERVICE_FAILURE_TYPE_OPTIONS}
                value={serviceFailureType}
                onChange={(next) => setServiceFailureType(next)}
                onClear={() => setServiceFailureType('')}
                placeholder="Select type…"
                disabled={saving}
                showSearch
                showDescriptions
                flagInvalidValue
              />
            </div>

            {showThdReason && (
            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                THD Reason<span className="text-red-500 ml-1">*</span>
              </label>
              <TypeAhead
                options={thdReasonOptions.map((r) => ({ value: r, label: r }))}
                value={thdReason}
                onChange={(next) => setThdReason(next)}
                onClear={() => setThdReason('')}
                placeholder="Select THD reason…"
                disabled={saving}
                showSearch
                flagInvalidValue
              />
            </div>
            )}

            <div>
              <label className="block text-xs font-medium text-muted mb-1">
                Responsible Driver
              </label>
              <TypeAhead
                options={driverOptions}
                value={responsibledriver}
                onChange={(val) => setResponsibleDriver(val)}
                onClear={() => setResponsibleDriver('')}
                placeholder="Search drivers…"
                disabled={saving}
                showSearch
                showDescriptions
                groupBy
                flagInvalidValue
              />
            </div>

            <div>
              <label
                htmlFor="rs-comments"
                className="block text-xs font-medium text-muted mb-1"
              >
                Comments<span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="rs-comments"
                rows={3}
                maxLength={2000}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                disabled={saving}
                className="block w-full text-sm rounded border border-slate-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 dark:disabled:bg-gray-800"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-label bg-panel border border-slate-300 rounded hover:bg-slate-50 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : isCreate ? 'Create' : 'Save'}
          </button>
        </div>
      </div>

      {reschedule && (
        <RecordHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          endpoint={`/scheduling/reschedules/${reschedule.recno}/history`}
          title={`Service Failure #${reschedule.recno} History`}
          subtitle={pronumber ? `PRO ${pronumber}` : undefined}
          fieldLabels={SF_HISTORY_FIELD_LABELS}
        />
      )}
    </div>,
    document.body,
  );
};

export default RescheduleEditModal;